import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { Helmet } from 'react-helmet-async';
import LoadingScreen from '../components/LoadingScreen';
import { useLang } from '../context/LanguageContext';

const About = () => {
  const { nl } = useLang();
  const L = (obj, field) => (nl && obj?.[field + '_nl']) || obj?.[field] || '';
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/seo/about`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setSeoData(data.data);
        }
      })
      .catch(err => console.error("SEO fetch error:", err));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aboutResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/aboutpage`)
        ]);
        setAboutData(aboutResponse.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <LoadingScreen/>;
  if (!aboutData) return <div>Error loading about page</div>;

  return (
    <div>
      <Helmet>
        <title>{seoData?.title || 'Aurelia Technology'}</title>
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.canonicalUrl && <link rel="canonical" href={seoData.canonicalUrl} />}
        <meta property="og:title" content={seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta property="og:description" content={seoData?.ogDescription || seoData?.description || ''} />
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData?.twitterTitle || seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta name="twitter:description" content={seoData?.twitterDescription || seoData?.ogDescription || seoData?.description || ''} />
        {(seoData?.twitterImage || seoData?.ogImage) && <meta name="twitter:image" content={seoData.twitterImage || seoData.ogImage} />}
      </Helmet>
      <style>{`
        .story-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          margin-top: 50px;
        }
        .story-text h3 {
          font-size: 2rem;
          color: #B28744;
          margin-bottom: 20px;
        }
        .story-text p {
          font-size: 1.1rem;
          line-height: 1.7;
          color: #161615;
          margin-bottom: 20px;
        }
        .story-image img {
          width: 100%;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }
        @media (max-width: 768px) {
          .story-content {
            grid-template-columns: 1fr;
            gap: 40px;
          }
        }
        .specialization-section, .services-section, .solutions {
          padding: 60px 0;
        }
        .company {
          padding: 60px 0;
        }
        .solutions .solutions-grid {
          display: flex;
          flex-direction: row;
          gap: 30px;
          width: 100%;
        }
        .solutions .solution-card {
          flex: 1;
          max-width: 50%;
          display: flex;
          flex-direction: column;
        }
        .solutions .solution-image {
          width: 100%;
        }
        .solutions .solution-content {
          flex: 1;
        }
        @media (max-width: 768px) {
          .solutions .solutions-grid {
            flex-direction: column;
          }
          .solutions .solution-card {
            max-width: 100%;
          }
        }
      `}</style>

{/* About Banner */}
      <section className="contact-banner" style={aboutData?.banner?.image ? {backgroundImage: `url(${aboutData.banner.image})`} : {}}>
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <div className="banner-left">
            <h1>{L(aboutData?.banner, 'title') || 'ABOUT Aurelia Technology'}</h1>
            <p>{L(aboutData?.banner, 'subtitle') || 'Pioneering the future with AI-driven solutions and blockchain innovation'}</p>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="specialization-section" style={{background: '#FFFAF1'}}>
        <div className="container">
          <div className="section-header">
            <h2 style={{color: '#090909'}}>{L(aboutData?.storySection, 'title') || 'Our Story'}</h2>
            <p style={{color: '#1d1c1a'}}>{L(aboutData?.storySection, 'subtitle') || 'Building innovative solutions that transform businesses worldwide'}</p>
            <div className="section-divider">
              <div className="divider-line"></div>
              <div className="divider-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          </div>
          
          <div className="story-content">
            <div className="story-text">
              <h3>{L(aboutData?.storySection, 'contentTitle') || 'Transforming Ideas into Digital Reality'}</h3>
              <p>{L(aboutData?.storySection, 'content') ? L(aboutData?.storySection, 'content').split('. ').slice(0, 2).join('. ') + '.' : 'Founded with a vision to bridge the gap between cutting-edge technology and real-world business solutions, Highon Innovation has been at the forefront of AI and blockchain development for over a decade.'}</p>
              <p>{L(aboutData?.storySection, 'content') ? L(aboutData?.storySection, 'content').split('. ').slice(2).join('. ') : 'We believe that technology should empower businesses to achieve their highest potential.'}</p>
            </div>
            <div className="story-image">
              <img src={aboutData?.storySection?.image || 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=500&h=400&fit=crop'} alt="Innovation Team" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="services-section" style={{background: '#FBFBF9'}}>
        <div className="container">
          <div className="section-header">
            <h2>{L(aboutData?.sections?.[0], 'title') || 'Our Core Values'}</h2>
            <p>{L(aboutData?.sections?.[0], 'subtitle') || 'The principles that guide everything we do'}</p>
            <div className="section-divider">
              <div className="divider-line"></div>
              <div className="divider-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          </div>
          
          <div className="services-cards">
            {aboutData?.sections?.[0]?.cards?.map((card, index) => (
              <div key={index} className="service-card">
                <div className="service-image">
                  <img src={card.image} alt={card.title} />
                </div>
                <h3>{L(card, 'title')}</h3>
                <p>{L(card, 'description')}</p>
              </div>
            )) || (
              <>
                <div className="service-card">
                  <div className="service-image">
                    <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=300&h=200&fit=crop" alt="Innovation" />
                  </div>
                  <h3>Innovation</h3>
                  <p>We constantly push boundaries and explore new possibilities in AI and blockchain technology to deliver cutting-edge solutions.</p>
                </div>
                <div className="service-card">
                  <div className="service-image">
                    <img src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=300&h=200&fit=crop" alt="Excellence" />
                  </div>
                  <h3>Excellence</h3>
                  <p>We strive for perfection in every project, delivering solutions that exceed expectations and drive business success.</p>
                </div>
                <div className="service-card">
                  <div className="service-image">
                    <img src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=300&h=200&fit=crop" alt="Collaboration" />
                  </div>
                  <h3>Collaboration</h3>
                  <p>We believe in the power of teamwork and partnership to achieve extraordinary results and lasting relationships.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section - Dynamic */}
      {((aboutData?.sections?.find(s => s.type === 'mission-vision')) && (() => {
        const missionVisionSection = aboutData.sections.find(s => s.type === 'mission-vision');
        return (
          <section className="solutions" style={{background: '#FBFBF9'}}>
            <div className="container">
              <div className="section-header">
                <h2>{L(missionVisionSection, 'title') || 'Mission & Vision'}</h2>
                <p>{L(missionVisionSection, 'subtitle') || 'Our commitment to transforming the future through technology'}</p>
                <div className="section-divider">
                  <div className="divider-line"></div>
                  <div className="divider-dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              </div>
              
              <div className="solutions-grid">
                {missionVisionSection.cards?.map((card, index) => (
                  <div key={index} className="solution-card">
                    <div className="solution-image">
                      <img src={card.image} alt={card.title} />
                    </div>
                    <div className="solution-content">
                      <h3>{L(card, 'title')}</h3>
                      <p>{L(card, 'description')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()) || (
        <section className="solutions" style={{background: '#FBFBF9'}}>
          <div className="container">
            <div className="section-header">
              <h2>Mission & Vision</h2>
              <p>Our commitment to transforming the future through technology</p>
              <div className="section-divider">
                <div className="divider-line"></div>
                <div className="divider-dots">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
            
            <div className="solutions-grid">
              <div className="solution-card">
                <div className="solution-image">
                  <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=300&h=200&fit=crop" alt="Our Mission" />
                </div>
                <div className="solution-content">
                  <h3>Our Mission</h3>
                  <p>To democratize advanced technology by making AI and blockchain solutions accessible to businesses of all sizes, enabling them to innovate, grow, and lead in their respective industries.</p>
                </div>
              </div>
              
              <div className="solution-card">
                <div className="solution-image">
                  <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=300&h=200&fit=crop" alt="Our Vision" />
                </div>
                <div className="solution-content">
                  <h3>Our Vision</h3>
                  <p>To be the global leader in AI and blockchain innovation, creating a world where intelligent technology seamlessly integrates with human potential to solve complex challenges.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Dynamic Sections */}
      {aboutData?.sections?.filter(s => s.type !== 'mission-vision' && s !== aboutData?.sections?.[0] && s.isActive !== false).map((section, index) => (
        <section key={index} className="services-section" style={{background: index % 2 === 0 ? '#FFFAF1' : '#FBFBF9'}}>
          <div className="container">
            <div className="section-header">
              <h2>{L(section, 'title')}</h2>
              {(L(section, 'subtitle')) && <p>{L(section, 'subtitle')}</p>}
              <div className="section-divider">
                <div className="divider-line"></div>
                <div className="divider-dots">
                  <span className="dot"></span><span className="dot"></span><span className="dot"></span><span className="dot"></span><span className="dot"></span>
                </div>
              </div>
            </div>
            {section.cards?.length > 0 && (
              <div className="services-cards">
                {section.cards.map((card, i) => (
                  <div key={i} className="service-card">
                    {card.image && <div className="service-image"><img src={card.image} alt={L(card, 'title')} /></div>}
                    <h3>{L(card, 'title')}</h3>
                    <p>{L(card, 'description')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Stats Section - Dynamic */}
      <section className="stats-section" style={{
        backgroundImage: aboutData?.statsSection?.backgroundImage 
          ? `url(${aboutData.statsSection.backgroundImage})` 
          : 'url(/images/Group9.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div className="container">
          <div className="stats-row">
            {(aboutData?.statsSection?.stats?.sort((a, b) => a.order - b.order).map((stat, index) => (
              <div key={index} className="stat">
                <span className="stat-number">{stat.number}{stat.suffix}</span>
                <span className="stat-label">{L(stat, 'label')}</span>
              </div>
            ))) || (
              <>
                <div className="stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Projects Delivered</span>
                </div>
                <div className="stat">
                  <span className="stat-number">150+</span>
                  <span className="stat-label">Happy Clients</span>
                </div>
                <div className="stat">
                  <span className="stat-number">10+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
                <div className="stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Team Members</span>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
